/**
 * Customer Support Chat Widget for Landing Page
 * 
 * A floating chat button that opens a conversational support interface.
 * - Quick AI chat without creating tickets
 * - Tickets created only when AI can't help or user requests
 * - Full ticket management for existing issues
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth, authFetch } from '../utils/auth';
import './SupportChat.css';

// ── Types ──────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface QuickChatResponse {
  response: string;
  needs_ticket: boolean;
  suggested_subject: string | null;
  suggested_category: string | null;
  confidence: number;
}

interface SupportTicket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  priority: string;
  category: string;
  handled_by_ai: boolean;
  escalated_to_human: boolean;
  created_at: string;
  updated_at: string;
}

interface SupportMessage {
  id: string;
  sender_type: 'user' | 'ai' | 'agent' | 'system';
  content: string;
  ai_confidence: number | null;
  was_helpful: boolean | null;
  created_at: string;
}

interface TicketDetail extends SupportTicket {
  messages: SupportMessage[];
}

const CATEGORIES = [
  { value: 'general', label: '💬 General' },
  { value: 'technical', label: '🔧 Technical Issue' },
  { value: 'account', label: '👤 Account' },
  { value: 'billing', label: '💳 Billing' },
  { value: 'feature', label: '✨ Feature Request' },
];

type ViewType = 'chat' | 'tickets' | 'ticket-detail' | 'create-ticket';

export default function SupportChat() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<ViewType>('chat');

  // Quick chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [suggestedTicket, setSuggestedTicket] = useState<{
    subject: string;
    category: string;
  } | null>(null);

  // Tickets state
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [currentTicket, setCurrentTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Create ticket form
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketCategory, setTicketCategory] = useState('general');
  const [ticketMessage, setTicketMessage] = useState('');

  // Reply input
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Unread indicator
  const [hasUnread, setHasUnread] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom(chatEndRef);
  }, [chatMessages]);

  useEffect(() => {
    scrollToBottom(messagesEndRef);
  }, [currentTicket?.messages]);

  // ── Quick Chat Functions ─────────────────────────────────────

  const sendQuickChat = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    setSuggestedTicket(null);
    setError(null);

    try {
      const response = await authFetch<QuickChatResponse>('/support/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: userMessage,
          history: chatMessages,
        }),
      });

      setChatMessages(prev => [...prev, { role: 'assistant', content: response.response }]);

      // If AI suggests a ticket, show the option
      if (response.needs_ticket && response.suggested_subject) {
        setSuggestedTicket({
          subject: response.suggested_subject,
          category: response.suggested_category || 'general',
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send message');
      // Remove the user message if it failed
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
    }
  };

  const createTicketFromChat = async () => {
    if (!suggestedTicket) return;

    setLoading(true);
    setError(null);

    try {
      const ticket = await authFetch<TicketDetail>('/support/chat/create-ticket', {
        method: 'POST',
        body: JSON.stringify({
          subject: suggestedTicket.subject,
          category: suggestedTicket.category,
          history: chatMessages,
        }),
      });

      setCurrentTicket(ticket);
      setSuggestedTicket(null);
      setView('ticket-detail');
      fetchTickets(); // Refresh ticket list
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const startFreshChat = () => {
    setChatMessages([]);
    setSuggestedTicket(null);
    setError(null);
    setView('chat');
  };

  // ── Ticket Functions ─────────────────────────────────────────

  const fetchTickets = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await authFetch<{ tickets: SupportTicket[] }>('/support/tickets');
      setTickets(data.tickets);
      
      // Check for unread (tickets awaiting user response)
      const hasAwaitingUser = data.tickets.some(t => t.status === 'awaiting_user');
      setHasUnread(hasAwaitingUser);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchTicketDetail = useCallback(async (ticketId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await authFetch<TicketDetail>(`/support/tickets/${ticketId}`);
      setCurrentTicket(data);
      setView('ticket-detail');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, []);

  const createTicketManually = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) return;

    setSending(true);
    setError(null);
    try {
      const data = await authFetch<TicketDetail>('/support/tickets', {
        method: 'POST',
        body: JSON.stringify({
          subject: ticketSubject,
          message: ticketMessage,
          category: ticketCategory,
        }),
      });
      setCurrentTicket(data);
      setTicketSubject('');
      setTicketMessage('');
      setTicketCategory('general');
      setView('ticket-detail');
      fetchTickets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create ticket');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !currentTicket) return;
    
    setSending(true);
    setError(null);
    try {
      await authFetch(`/support/tickets/${currentTicket.id}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          message: replyMessage,
        }),
      });
      
      setReplyMessage('');
      await fetchTicketDetail(currentTicket.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const escalateTicket = async () => {
    if (!currentTicket) return;
    
    try {
      await authFetch(`/support/tickets/${currentTicket.id}/escalate`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'User requested human support' }),
      });
      await fetchTicketDetail(currentTicket.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to escalate');
    }
  };

  const resolveTicket = async () => {
    if (!currentTicket) return;
    
    try {
      await authFetch(`/support/tickets/${currentTicket.id}/resolve`, {
        method: 'POST',
      });
      await fetchTicketDetail(currentTicket.id);
      fetchTickets();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resolve');
    }
  };

  const provideFeedback = async (messageId: string, wasHelpful: boolean) => {
    try {
      await authFetch(`/support/messages/${messageId}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ was_helpful: wasHelpful }),
      });
      if (currentTicket) {
        await fetchTicketDetail(currentTicket.id);
      }
    } catch (e) {
      console.error('Failed to submit feedback:', e);
    }
  };

  // Fetch tickets when opening if user is logged in
  useEffect(() => {
    if (isOpen && user) {
      fetchTickets();
    }
  }, [isOpen, user, fetchTickets]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setView('chat');
      setCurrentTicket(null);
      setError(null);
      // Don't clear chat messages - keep them for when user reopens
    }
  }, [isOpen]);

  // ── Helpers ──────────────────────────────────────────────────

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#3b82f6';
      case 'awaiting_user': return '#f59e0b';
      case 'awaiting_agent': return '#8b5cf6';
      case 'resolved': return '#10b981';
      case 'closed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getSenderIcon = (senderType: string) => {
    switch (senderType) {
      case 'user': return '👤';
      case 'ai': return '🤖';
      case 'agent': return '👨‍💼';
      case 'system': return 'ℹ️';
      default: return '💬';
    }
  };

  const getHeaderTitle = () => {
    if (!user) return '💬 Support';
    switch (view) {
      case 'chat': return '💬 Chat with AI';
      case 'tickets': return '📋 My Tickets';
      case 'ticket-detail': return currentTicket ? `#${currentTicket.ticket_number}` : 'Ticket';
      case 'create-ticket': return '✨ Create Ticket';
      default: return '💬 Support';
    }
  };

  const openTicketCount = tickets.filter(t => !['resolved', 'closed'].includes(t.status)).length;

  // ── Render ───────────────────────────────────────────────────

  return (
    <>
      {/* Floating Chat Button */}
      <button 
        className={`support-chat-fab ${hasUnread ? 'has-unread' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Open support chat"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
        {hasUnread && <span className="support-chat-fab-badge" />}
      </button>

      {/* Chat Modal */}
      {isOpen && (
        <div className="support-chat-overlay" onClick={() => setIsOpen(false)}>
          <div className="support-chat-container" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="support-chat-header">
              <div className="support-chat-header-left">
                {(view === 'ticket-detail' || view === 'create-ticket') && user && (
                  <button
                    className="support-back-btn"
                    onClick={() => {
                      if (view === 'ticket-detail') {
                        setView('tickets');
                      } else {
                        setView('chat');
                      }
                      setCurrentTicket(null);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
                <h2>{getHeaderTitle()}</h2>
              </div>
              <button className="support-close-btn" onClick={() => setIsOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Navigation tabs for authenticated users */}
            {user && (view === 'chat' || view === 'tickets') && (
              <div className="support-nav-tabs">
                <button
                  className={`support-nav-tab ${view === 'chat' ? 'active' : ''}`}
                  onClick={() => setView('chat')}
                >
                  💬 Chat
                </button>
                <button
                  className={`support-nav-tab ${view === 'tickets' ? 'active' : ''}`}
                  onClick={() => { setView('tickets'); fetchTickets(); }}
                >
                  📋 Tickets {openTicketCount > 0 && <span className="ticket-count">{openTicketCount}</span>}
                </button>
              </div>
            )}

            {/* Error banner */}
            {error && (
              <div className="support-error">
                {error}
                <button onClick={() => setError(null)}>×</button>
              </div>
            )}

            {/* Content */}
            <div className="support-chat-content">
              {/* Guest View - Not logged in */}
              {!user && (
                <div className="support-guest-view">
                  <div className="support-guest-icon">💬</div>
                  <h3>Need Help?</h3>
                  <p>Sign in to your account to chat with our AI-powered support team.</p>
                  <a href="/login" className="support-login-btn">
                    Sign In
                  </a>
                  <p className="support-guest-hint">
                    Don't have an account? <a href="/register">Create one free</a>
                  </p>
                </div>
              )}

              {/* Authenticated Views */}
              {user && (
                <>
                  {/* Quick Chat View */}
                  {view === 'chat' && (
                    <>
                      <div className="support-quick-chat">
                        {chatMessages.length === 0 ? (
                          <div className="support-chat-welcome">
                            <div className="support-chat-welcome-icon">🤖</div>
                            <h3>Hi! How can I help?</h3>
                            <p>Ask me anything about Intervio. I'll do my best to help, and if I can't, I'll connect you with our support team.</p>
                            <div className="support-quick-suggestions">
                              <button onClick={() => setChatInput('How do I start an interview session?')}>
                                How to start a session?
                              </button>
                              <button onClick={() => setChatInput('My trial expired, can I get more time?')}>
                                Extend my trial
                              </button>
                              <button onClick={() => setChatInput('The app is not detecting questions')}>
                                Questions not detected
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="support-chat-messages">
                            {chatMessages.map((msg, idx) => (
                              <div key={idx} className={`support-chat-msg ${msg.role}`}>
                                <span className="support-chat-msg-icon">
                                  {msg.role === 'user' ? '👤' : '🤖'}
                                </span>
                                <div className="support-chat-msg-content">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {msg.content}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            ))}
                            {chatLoading && (
                              <div className="support-chat-msg assistant">
                                <span className="support-chat-msg-icon">🤖</span>
                                <div className="support-chat-msg-content">
                                  <div className="support-typing-indicator">
                                    <span></span><span></span><span></span>
                                  </div>
                                </div>
                              </div>
                            )}
                            <div ref={chatEndRef} />
                          </div>
                        )}

                        {/* Suggested ticket banner */}
                        {suggestedTicket && (
                          <div className="support-suggested-ticket">
                            <p>🎫 It looks like this needs more attention. Would you like to create a support ticket?</p>
                            <div className="support-suggested-ticket-actions">
                              <button
                                className="support-btn-primary"
                                onClick={createTicketFromChat}
                                disabled={loading}
                              >
                                {loading ? 'Creating...' : 'Create Ticket'}
                              </button>
                              <button
                                className="support-btn-secondary"
                                onClick={() => setSuggestedTicket(null)}
                              >
                                Continue Chat
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Chat input */}
                      <div className="support-chat-input-bar">
                        <input
                          type="text"
                          placeholder="Type your question..."
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendQuickChat();
                            }
                          }}
                          disabled={chatLoading}
                        />
                        <button
                          onClick={sendQuickChat}
                          disabled={chatLoading || !chatInput.trim()}
                        >
                          {chatLoading ? '...' : '➤'}
                        </button>
                      </div>

                      {/* Actions below chat */}
                      {chatMessages.length > 0 && (
                        <div className="support-chat-actions">
                          <button onClick={startFreshChat} className="support-link-btn">
                            Start new conversation
                          </button>
                          <button
                            onClick={() => {
                              setTicketSubject('');
                              setTicketMessage('');
                              setView('create-ticket');
                            }}
                            className="support-link-btn"
                          >
                            Create ticket manually
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Tickets List View */}
                  {view === 'tickets' && (
                    <>
                      {loading ? (
                        <div className="support-loading">
                          <div className="support-loading-spinner" />
                          Loading...
                        </div>
                      ) : tickets.length === 0 ? (
                        <div className="support-empty">
                          <div className="support-empty-icon">📭</div>
                          <p>No support tickets yet.</p>
                          <p className="support-empty-hint">Chat with our AI first - tickets are created only when needed!</p>
                        </div>
                      ) : (
                        <div className="support-ticket-list">
                          {tickets.map((ticket) => (
                            <button
                              key={ticket.id}
                              className="support-ticket-item"
                              onClick={() => fetchTicketDetail(ticket.id)}
                            >
                              <div className="support-ticket-item-header">
                                <span 
                                  className="support-ticket-status"
                                  style={{ backgroundColor: getStatusColor(ticket.status) }}
                                >
                                  {ticket.status.replace('_', ' ')}
                                </span>
                                <span className="support-ticket-date">
                                  {formatDate(ticket.updated_at)}
                                </span>
                              </div>
                              <div className="support-ticket-subject">{ticket.subject}</div>
                              <div className="support-ticket-meta">
                                <span className="support-ticket-number">#{ticket.ticket_number}</span>
                                {ticket.handled_by_ai && <span className="support-ai-badge">🤖 AI</span>}
                                {ticket.escalated_to_human && <span className="support-human-badge">👨‍💼 Human</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Create Ticket View */}
                  {view === 'create-ticket' && (
                    <div className="support-new-ticket-form">
                      <div className="support-form-group">
                        <label>Category</label>
                        <select
                          value={ticketCategory}
                          onChange={(e) => setTicketCategory(e.target.value)}
                        >
                          {CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="support-form-group">
                        <label>Subject</label>
                        <input
                          type="text"
                          placeholder="Brief description of your issue"
                          value={ticketSubject}
                          onChange={(e) => setTicketSubject(e.target.value)}
                          maxLength={255}
                        />
                      </div>

                      <div className="support-form-group">
                        <label>Message</label>
                        <textarea
                          placeholder="Describe your issue in detail..."
                          value={ticketMessage}
                          onChange={(e) => setTicketMessage(e.target.value)}
                          rows={6}
                        />
                      </div>

                      <button
                        className="support-submit-btn"
                        onClick={createTicketManually}
                        disabled={sending || !ticketSubject.trim() || !ticketMessage.trim()}
                      >
                        {sending ? 'Creating...' : 'Create Ticket'}
                      </button>

                      <p className="support-ai-notice">
                        💡 Tip: Try chatting with our AI first - many issues can be resolved instantly!
                      </p>
                    </div>
                  )}

                  {/* Ticket Detail View */}
                  {view === 'ticket-detail' && currentTicket && (
                    <>
                      {/* Ticket info bar */}
                      <div className="support-ticket-info-bar">
                        <span className="support-ticket-subject-bar">{currentTicket.subject}</span>
                        <div className="support-ticket-actions">
                          {!currentTicket.escalated_to_human && currentTicket.status !== 'resolved' && currentTicket.status !== 'closed' && (
                            <button 
                              className="support-action-btn escalate"
                              onClick={escalateTicket}
                              title="Request human support"
                            >
                              👨‍💼 Human
                            </button>
                          )}
                          {currentTicket.status !== 'resolved' && currentTicket.status !== 'closed' && (
                            <button 
                              className="support-action-btn resolve"
                              onClick={resolveTicket}
                              title="Mark as resolved"
                            >
                              ✓ Resolved
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Messages */}
                      <div className="support-messages">
                        {currentTicket.messages.map((msg) => (
                          <div 
                            key={msg.id} 
                            className={`support-message ${msg.sender_type}`}
                          >
                            <div className="support-message-header">
                              <span className="support-message-sender">
                                {getSenderIcon(msg.sender_type)}
                                {msg.sender_type === 'user' ? 'You' : 
                                 msg.sender_type === 'ai' ? 'AI Assistant' :
                                 msg.sender_type === 'agent' ? 'Support Agent' : 'System'}
                              </span>
                              <span className="support-message-time">{formatDate(msg.created_at)}</span>
                            </div>
                            <div className="support-message-content">
                              {msg.sender_type === 'system' ? (
                                <em>{msg.content}</em>
                              ) : (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {msg.content}
                                </ReactMarkdown>
                              )}
                            </div>
                            {/* Feedback buttons for AI messages */}
                            {msg.sender_type === 'ai' && msg.was_helpful === null && (
                              <div className="support-message-feedback">
                                <span>Was this helpful?</span>
                                <button onClick={() => provideFeedback(msg.id, true)}>👍</button>
                                <button onClick={() => provideFeedback(msg.id, false)}>👎</button>
                              </div>
                            )}
                            {msg.sender_type === 'ai' && msg.was_helpful !== null && (
                              <div className="support-message-feedback-result">
                                {msg.was_helpful ? '👍 Thanks for the feedback!' : '👎 We\'ll improve!'}
                              </div>
                            )}
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* Reply input */}
                      {currentTicket.status !== 'closed' && (
                        <div className="support-reply-bar">
                          <input
                            type="text"
                            placeholder="Type your message..."
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendReply();
                              }
                            }}
                            disabled={sending}
                          />
                          <button 
                            onClick={sendReply}
                            disabled={sending || !replyMessage.trim()}
                          >
                            {sending ? '...' : '➤'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
